import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { DAY_MONTH_NAME } from 'constants/dateFormats'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import OfferFilterSelectBox from 'components/OfferPage/Filters/OfferFilterSelectBox'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'

const SizedFilterSelectBox = styled(OfferFilterSelectBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  isOpen: boolean;
  inSidebar?: boolean;
}

const CalendarDatePickerSelectBox = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    checkInDate,
    checkOutDate,
    isOpen,
    inSidebar,
  } = props
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const checkIn = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOut = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const selectedDuration = checkOut?.diff(checkIn, 'days')
  const areDatesValid = checkIn && checkOut
  const dispatch = useOfferPageDispatch()

  const onClick = useCallback(() => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
    }
  }, [dispatch, isOpen, trackEvent])

  return (
    <SizedFilterSelectBox
      onClick={onClick}
      active={isOpen}
      ref={ref}
      id="date-picker"
      data-testid="CalendarDatePickerSelectBox"
      icon={<LineCalendarIcon />}
      inSidebar={inSidebar}
      label="When"
      placeholder="Select your dates"
      value={areDatesValid ? <><b>{checkIn.format(DAY_MONTH_NAME)} - {checkOut.format(DAY_MONTH_NAME)}</b> ({pluralizeToString('night', selectedDuration)})</> : undefined}
    />
  )
})

CalendarDatePickerSelectBox.displayName = 'CalendarDatePickerSelectBox'

export default withOfferPageState(state => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  isOpen: state.activeFilter === 'checkin',
}), CalendarDatePickerSelectBox)
